<template>
  <div :class="$style.checkboxTree">
    <template
      v-for="definition in localDefs"
      :key="definition.key + '-' + updateKey"
    >
      <div
        :style="{marginLeft: marginLevelOffset + 'px'}"
        class="sb-checkbox-tree"
      >
        <sb-checkbox
          v-model:checked="definition.checked"
          :version="dsComponentDetails.version"
          v-bind="getCheckboxProps(definition)"
          :name="definition.key"
          :data-testid="definition.key"
          :indeterminate="definition.indeterminate"
          @update:checked="updateRawValue($event, definition)"
        >
          <sb-text :value="definition.name" />
        </sb-checkbox>
        <checkbox-tree
          :version="dsComponentDetails.version"
          :filter="getChildrenProps(definition)"
          :margin-level-offset="25"
          :parent-key="getItemKey(definition)"
          :value="value"
          :level="level + 1"
          @update:value="updateChild($event, definition)"
        />
      </div>
    </template>
  </div>
</template>

<script>
import SbCheckbox from '@src/components/Checkbox/SbCheckbox.vue';
import SbText from '@src/components/Text/SbText.vue';
import { versionedComponentsMixin } from '@src/mixins/versionedComponents';

export default {
  name: 'CheckboxTree',
  components: {
    SbCheckbox,
    SbText,
  },
  mixins: [versionedComponentsMixin],
  props: {
    value: {
      type: Object,
      required: true,
      default: () => {},
    },
    filter: {
      type: Object,
      required: true,
      default: () => {},
    },

    /* Tree Recursive props */
    marginLevelOffset: {
      type: Number,
      default: 0,
    },
    parentKey: {
      type: String,
      default: '',
    },
    level: {
      type: Number,
      default: 1,
    },
  },
  emits: ['update:value'],
  data() {
    return {
      localDefs: [],
      updateKey: 0,
    };
  },
  watch: {
    value(newValue = []) {
      const { list, hasChanged } = this.runSetValue(this.localDefs, newValue);
      if (hasChanged) this.localDefs = list;
    },
  },
  mounted() {
    const { list } = this.runSetValue(this.filter.options, this.value);
    this.localDefs = list;
  },
  methods: {
    runSetValue(options, newValue) {
      const mappedByKey = newValue?.reduce((prev, curr) => {
        const previousValue = prev;
        previousValue[curr.key] = curr;
        return previousValue;
      }, {});
      return this.setValue(options, mappedByKey);
    },
    setValue(list, mapChangesParam, emitted) {
      const mapChanges = mapChangesParam || {};
      let hasChanged = false;
      const newList = list?.map((sb) => {
        const { list: childrenList, hasChanged: childChanged } = this.setValue(sb.children, mapChanges, emitted);
        const { indeterminate, checked } = mapChanges[sb.key]
                                || (!emitted ? { indeterminate: false, checked: false } : sb);
        if (childChanged || checked !== sb.checked || indeterminate !== sb.indeterminate) {
          hasChanged = true;
          return {
            ...sb,
            checked,
            indeterminate,
            children: childrenList,
          };
        }
        return sb;
      });
      return { list: newList, hasChanged };
    },
    getItemKey(definition) {
      return this.parentKey ? `${this.parentKey}.${definition.key}` : definition.key;
    },
    getCheckboxProps(definition) {
      const { props = {} } = this.filter;
      if (typeof props === 'function') {
        const key = this.getItemKey(definition);
        return props(key, key.split('.').length);
      }
      return props;
    },
    getChildrenProps(definition) {
      const { props } = this.filter;
      return { options: definition.children, props };
    },
    checkChildrenFlags(definition) {
      let isIndeterminate = false;
      let allTrue = true;
      let allFalse = true;
      for (let index = 0; index < definition.children?.length; index += 1) {
        const child = definition.children[index];
        allFalse = allFalse && !child.checked;
        allTrue = allTrue && !!child.checked;
        isIndeterminate = isIndeterminate || !!child.indeterminate;
      }
      return { isIndeterminate: isIndeterminate || !(allTrue || allFalse), allTrue, allFalse };
    },
    updateRawValue(value, definitionParam, notEmit) {
      const definition = definitionParam;
      const newValue = definition.indeterminate ? true : !value;

      definition.checked = newValue;
      definition.indeterminate = false;

      let changesList = [];
      definition.children?.forEach((node) => {
        if (node.checked !== definition.checked) {
          changesList = changesList.concat(this.updateRawValue(node.checked, node, true));
        }
      });

      this.updateKey = Date.now();
      changesList.unshift({
        checked: newValue,
        key: definition.key,
        indeterminate: definition.indeterminate,
        name: definition.name,
        hasChildren: (definition.children?.length > 0) || false,
      });

      if (!notEmit) this.$emit('update:value', changesList);

      return changesList;
    },
    updateChild(object, definitionParam) {
      const definition = definitionParam;

      const { isIndeterminate, allTrue } = this.checkChildrenFlags(definition);
      const newValue = !!(isIndeterminate || allTrue);
      definition.checked = newValue;
      definition.indeterminate = isIndeterminate;

      this.$emit('update:value', [{ checked: newValue,
        key: definition.key,
        indeterminate: isIndeterminate,
        hasChildren: true,
        name: definition.name },
      ...object]);
    },
  },
};
</script>

<style lang="scss" module>
.checkboxTree :global(.main-checkbox) {
  font-weight: bold;
}
</style>
